<template>
  <div class="about-us-page-container">
    <!-- 页面级背景容器 -->
    <div class="page-content-wrapper">
      <!-- 内容约束和可能的内层背景/叠加层 -->

      <section class="who-we-are-section content-section" id="who-we-are">
        <h2 class="main-title">{{ $t("aboutPage.whoWeAre.title") }}</h2>
        <p class="sub-title">{{ $t("aboutPage.whoWeAre.subTitle") }}</p>
        <div class="content-wrapper">
          <p class="paragraph">{{ $t("aboutPage.whoWeAre.p1") }}</p>
          <p class="paragraph">{{ $t("aboutPage.whoWeAre.p2") }}</p>
        </div>
      </section>

      <section class="our-team-section content-section" id="our-team">
        <h3 class="section-header">{{ $t("aboutPage.ourTeam.title") }}</h3>
        <div class="team-gallery">
          <img
            v-for="(image, index) in teamImages"
            :key="index"
            :src="image.src"
            :alt="image.alt"
            class="team-image"
          />
        </div>
      </section>

      <section class="our-strengths-section content-section" id="our-strengths">
        <h3 class="section-header">
          {{ $t("aboutPage.ourStrengths.title") || "Our Strengths" }}
        </h3>
        <div class="grid-container">
          <div
            class="strength-item"
            v-for="strength in strengths"
            :key="strength.id"
          >
            <div class="strength-image-wrapper">
              <img
                :src="strength.image"
                :alt="strength.title"
                class="strength-image"
              />
            </div>
            <div class="strength-text-content">
              <span class="strength-number">0{{ strength.id }}</span>
              <h4 class="strength-title">{{ strength.title }}</h4>
              <p class="strength-description">{{ strength.description }}</p>
            </div>
          </div>
        </div>
      </section>

      <stats-section id="about-page-stats" class="content-section" />
    </div>
  </div>
</template>

<script>
import StatsSection from "../components/StatsSection.vue";

export default {
  name: "AboutUsPage",
  components: {
    StatsSection,
  },
  computed: {
    teamImages() {
      const imageKeys = ["team1", "team2", "team3", "team4", "team5"];
      return imageKeys.map((key) => {
        let imageSrc = "";
        try {
          imageSrc = require(`@/assets/about-images/${key}.jpg`);
        } catch (e) {
          console.warn(
            `团队图片未找到: src/assets/about-images/${key}.jpg. 使用占位符。`
          );
          imageSrc = `https://via.placeholder.com/200x150.png?text=${key}`;
        }
        return {
          src: imageSrc,
          alt:
            this.$t(`aboutPage.ourTeam.imageAlt.${key}`) ||
            `Team Member ${key.replace("team", "")}`,
        };
      });
    },
    strengths() {
      const i18nStrengths = this.$t("aboutPage.ourStrengths.items");
      if (Array.isArray(i18nStrengths)) {
        return i18nStrengths.map((item) => {
          let imageSrc = "";
          try {
            imageSrc = require(`@/assets/about-images/strength-${item.id}.png`);
          } catch (e) {
            console.warn(
              `优势图片未找到: src/assets/about-images/strength-${item.id}.png. 使用占位符。`
            );
            imageSrc = `https://via.placeholder.com/180x120.png?text=Strength ${item.id}`;
          }
          return {
            id: item.id,
            title: item.title,
            description: item.description,
            image: imageSrc,
          };
        });
      }
      return [];
    },
  },
};
</script>

<style scoped>
.about-us-page-container {
  min-height: 100vh;
  padding-top: 80px; /* 给页面顶部一些空间 */
  padding-bottom: 80px; /* 给页面底部一些空间 */
  box-sizing: border-box;
  overflow-x: hidden; /* 防止水平滚动条 */
  background-color: #f8f9fa; /* 替换为纯色背景 */
}

/* 新增：用于包裹所有内容的内部容器，可以设置最大宽度和居中 */
.page-content-wrapper {
  max-width: 1200px; /* 稍微增加最大宽度 */
  margin: 0 auto;
  padding: 0 20px; /* 容器左右内边距 */
}

/* 通用区块样式 */
.content-section {
  background-color: #ffffff; /* 改回纯白背景 */
  padding: 80px 50px; /* 进一步增加内边距让内容更舒展 */
  border-radius: 24px; /* 增加圆角 */
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.04); /* 减轻阴影 */
  margin-bottom: 60px; /* 区块之间的统一间距加大 */
  position: relative;
  z-index: 1;
  border: 1px solid #f1f5f9; /* 移除透明边框，换为实色细线 */
}
.content-section:last-child {
  margin-bottom: 0; /* 最后一个区块不需要下边距 */
}

/* 标题样式 */
.main-title,
.section-header {
  text-align: center;
  color: #1a1a2e; /* 深蓝黑色标题 */
  font-family: "Acumin Variable Concept", "Inter", sans-serif; /* 假设已加载 */
  font-weight: 700;
  margin-bottom: 15px;
}
.main-title {
  font-size: 3em; /* 主标题稍大 */
  margin-bottom: 15px; /* 主标题和副标题间距 */
  letter-spacing: -0.5px;
}
.sub-title {
  font-size: 1.25em;
  text-align: center;
  color: #007bff; /* 使用品牌蓝 */
  margin-bottom: 40px;
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 1.5px;
}
.section-header {
  /* 用于 "Our Team" 和 "Our Strengths" 的标题 */
  font-size: 2.2em;
  padding-bottom: 12px;
  border-bottom: 4px solid #007bff; /* 统一品牌蓝色下划线，稍微加粗 */
  display: inline-block; /* 使下划线只作用于文字宽度 */
  margin-bottom: 40px;
  /* 为了让 display:inline-block 的元素居中 */
  display: table;
  margin-left: auto;
  margin-right: auto;
  border-radius: 2px;
}

/* Who We Are Section */
.who-we-are-section .content-wrapper {
  /* content-wrapper 已被 .content-section 替代大部分样式 */
  max-width: 900px; /* 可以限制此特定区块的文本宽度 */
  margin: 0 auto;
  font-size: 1.15em;
  line-height: 1.9;
  color: #4a5568;
}
.who-we-are-section .paragraph {
  margin-bottom: 1.5em;
  text-align: center; /* 居中可能更有关于我们的氛围 */
}

/* Our Team Section */
.team-gallery {
  display: grid;
  grid-template-columns: repeat(
    auto-fill,
    minmax(200px, 1fr) /* 略微加大图片基础尺寸 */
  ); 
  gap: 24px;
}
.team-image {
  width: 100%;
  border-radius: 16px;
  aspect-ratio: 1 / 1; /* 正方形看起来更整齐 */
  object-fit: cover;
  object-position: top center; /* 人物图片通常偏上 */
  display: block;
  background-color: #f1f5f9;
  box-shadow: 0 5px 15px rgba(0,0,0,0.05); /* 减轻初始阴影 */
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}
.team-image:hover {
  transform: translateY(-5px); /* 降低上浮高度 */
  box-shadow: 0 12px 25px rgba(0,0,0,0.1); /* 阴影变淡 */
}

/* Our Strengths Section */
/* .our-strengths-section 的背景等由 .content-section 控制 */
.our-strengths-section .grid-container {
  /* 新增一个内部grid容器 */
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(450px, 1fr)); /* 稍微加宽单列最小宽度 */
  gap: 30px;
}

.strength-item {
  display: flex;
  flex-direction: column; /* 改为上下排列 */
  align-items: stretch; /* 占满宽度 */
  background-color: #f8f9fa; /* 改为浅灰背景，因为外层已经是白色了 */
  padding: 25px; /* 稍微减小整体内边距 */
  border-radius: 16px;
  box-shadow: none; /* 移除卡片初始阴影 */
  border: 1px solid #e2e8f0;
  transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
  position: relative; 
}
.strength-item:hover {
  transform: translateY(-5px); /* 降低上浮高度 */
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08); /* 减轻阴影 */
  border-color: #cbd5e0;
  background-color: #ffffff; /* 悬浮时变白 */
}

.strength-image-wrapper {
  flex-shrink: 0;
  width: 100%; /* 撑满容器宽度 */
  height: 240px; /* 增加高度以更好地适应全图展示 */
  border-radius: 12px;
  overflow: hidden; /* 确保图片在圆角内 */
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #ffffff; /* 托底色改白 */
  transition: transform 0.3s ease;
  margin-bottom: 20px; /* 图片和下方文字的间距 */
}

.strength-item:hover .strength-image-wrapper {
  transform: translateY(-5px); /* 悬浮时整个图片容器稍微上浮 */
}

.strength-image {
  width: 100%; /* 撑满宽度 */
  height: 100%; /* 撑满高度 */
  object-fit: contain; /* 改为 contain，确保图片完整展示不被裁切 */
  padding: 10px; /* 如果需要，可以加一点内边距让图片不要太贴边 */
  box-sizing: border-box;
  transition: transform 0.5s ease;
}

.strength-item:hover .strength-image {
  transform: scale(1.05); /* 悬浮时图片轻微放大 */
}

.strength-text-content {
  flex-grow: 1;
  padding-top: 10px;
  position: static; 
}
.strength-number {
  font-size: 3em; /* 稍微再大一点 */
  font-weight: 900; 
  color: #ffffff; /* 变为空心字效果的辅助色 */
  -webkit-text-stroke: 2px rgba(0, 123, 255, 0.15); /* 描边 */
  position: absolute;
  top: 220px; /* 随着wrapper高度调整位置 */
  right: 30px;
  line-height: 1;
  transition: transform 0.3s ease, -webkit-text-stroke 0.3s ease;
}
.strength-item:hover .strength-number {
  -webkit-text-stroke: 2px rgba(0, 123, 255, 0.4);
  transform: translateY(-5px);
}

.strength-title {
  font-size: 1.4em;
  color: #1a1a2e;
  font-weight: 700; /* 标题加粗 */
  margin-bottom: 12px;
}
.strength-description {
  font-size: 1.05em;
  line-height: 1.7;
  color: #4a5568;
}

/* StatsSection (假设它有自己的内边距和样式来适应) */

@media (max-width: 992px) {
  .our-strengths-section .grid-container {
    grid-template-columns: 1fr;
  }
  .page-content-wrapper {
    padding: 0 15px;
  }
}
@media (max-width: 768px) {
  .about-us-page-container {
    padding-top: 40px;
    padding-bottom: 40px;
  }
  .content-section {
    padding: 30px;
    margin-bottom: 40px;
  }
  .main-title {
    font-size: 2em;
  }
  .section-header {
    font-size: 1.6em;
    margin-bottom: 25px;
  }
  .sub-title {
    font-size: 1em;
    margin-bottom: 30px;
  }

  .who-we-are-section .content-wrapper {
    padding: 20px;
    font-size: 1em;
  }
  .team-gallery {
    grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
    gap: 15px;
  }
  .strength-item {
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 25px;
  }
  .strength-image-wrapper {
    margin-bottom: 20px;
    width: 100%; /* 移动端也撑满 */
    height: 180px; /* 移动端稍微减小高度 */
  }
  .strength-number {
    position: static;
    display: block;
    opacity: 1;
    font-size: 2em;
    margin-bottom: 10px;
    text-align: center;
  }
  .strength-item:hover .strength-number {
    opacity: 1;
  }
  .our-strengths-section .grid-container {
    gap: 20px;
  }
}
</style>
